import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { MailX, CheckCircle2, AlertCircle } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";

export default function UnsubscribePage() {
  const { token } = useParams();
  const [state, setState] = useState("idle"); // idle | busy | done | fail
  const [err, setErr] = useState("");

  const confirm = async () => {
    setState("busy");
    setErr("");
    try {
      await api.post(`/briefings/unsubscribe/${encodeURIComponent(token)}`);
      setState("done");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setState("fail");
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-6" data-testid="unsubscribe-page">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <div className="mx-auto flex items-center justify-center">
          <RecrystaMark size={48} />
        </div>

        {state === "idle" && (
          <>
            <div className="mt-4 mx-auto h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
              <MailX className="h-5 w-5 text-amber-700" />
            </div>
            <h1 className="mt-4 text-xl font-bold text-slate-900">Unsubscribe from briefings?</h1>
            <p className="mt-2 text-sm text-slate-600">
              You&apos;ll stop receiving the Monday briefing at your email address. You can re-enable it any time from Settings.
            </p>
            <button
              onClick={confirm}
              data-testid="unsubscribe-confirm"
              className="mt-6 w-full rounded-lg bg-slate-900 text-white py-2.5 text-sm font-semibold hover:bg-slate-800"
            >
              Yes, unsubscribe
            </button>
            <Link
              to="/"
              data-testid="unsubscribe-cancel"
              className="mt-3 inline-block text-sm text-slate-500 hover:text-slate-900"
            >
              No, take me home
            </Link>
          </>
        )}

        {state === "busy" && <p className="mt-4 text-sm text-slate-500">Processing…</p>}

        {state === "done" && (
          <>
            <div className="mt-4 mx-auto h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <h1 className="mt-4 text-xl font-bold text-slate-900" data-testid="unsubscribe-done">You&apos;re unsubscribed</h1>
            <p className="mt-2 text-sm text-slate-500">We won&apos;t send any more Monday briefings to this address.</p>
          </>
        )}

        {state === "fail" && (
          <>
            <div className="mt-4 mx-auto h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <h1 className="mt-4 text-xl font-bold text-slate-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="unsubscribe-error">{err}</p>
          </>
        )}
      </div>
    </div>
  );
}
